import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from './index';
import type { PaperJob, PaperPricing, PaperSection, PaperWizardStep } from '../types/paperWrite';

interface PaperWriteState {
  pricing: PaperPricing | null;
  job: PaperJob | null;
  jobs: PaperJob[];
  sections: PaperSection[];
  wizardStep: PaperWizardStep;
  isLoading: boolean;
  loadingMessage: string;
  error: string | null;

  /** Смонтирован ли сейчас экран автонаписания — чтобы не дублировать тост о готовности,
   *  когда пользователь и так смотрит на завершение вживую (см. socketSlice.ts). */
  isPageActive: boolean;
}

const initialState: PaperWriteState = {
  pricing: null,
  job: null,
  jobs: [],
  sections: [],
  wizardStep: 'form',
  isLoading: false,
  loadingMessage: '',
  error: null,

  isPageActive: false,
};

const paperWriteSlice = createSlice({
  name: 'paperWrite',
  initialState,
  reducers: {
    setPaperPricing(state, action: PayloadAction<PaperPricing>) {
      state.pricing = action.payload;
    },
    setPaperJob(state, action: PayloadAction<PaperJob | null>) {
      state.job = action.payload;
    },
    patchPaperJob(state, action: PayloadAction<Partial<PaperJob> & { id?: string }>) {
      if (!state.job) return;
      if (action.payload.id && action.payload.id !== state.job.id) return;
      Object.assign(state.job, action.payload);
    },
    setPaperJobs(state, action: PayloadAction<PaperJob[]>) {
      state.jobs = action.payload;
    },
    setPaperSections(state, action: PayloadAction<PaperSection[]>) {
      state.sections = action.payload;
    },
    setPaperWizardStep(state, action: PayloadAction<PaperWizardStep>) {
      state.wizardStep = action.payload;
    },
    setPaperLoading(state, action: PayloadAction<{ loading: boolean; message?: string }>) {
      state.isLoading = action.payload.loading;
      state.loadingMessage = action.payload.message || '';
      if (action.payload.loading) state.error = null;
    },
    setPaperError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
      state.isLoading = false;
    },
    setPaperPageActive(state, action: PayloadAction<boolean>) {
      state.isPageActive = action.payload;
    },
    resetPaperWrite(state) {
      Object.assign(state, {
        ...initialState,
        pricing: state.pricing,
        jobs: state.jobs,
        isPageActive: state.isPageActive,
      });
    },
  },
});

export const {
  setPaperPricing,
  setPaperJob,
  patchPaperJob,
  setPaperJobs,
  setPaperSections,
  setPaperWizardStep,
  setPaperLoading,
  setPaperError,
  setPaperPageActive,
  resetPaperWrite,
} = paperWriteSlice.actions;

export const selectPaperWrite = (state: RootState) => state.paperWrite;

export default paperWriteSlice.reducer;
